import { JobFile, JobLineItem, JobV2 } from "../@types/job";

// Form file arrays mix stored references with raw browser File objects (new
// uploads), which have a name but no url.
type AnyFile = { name?: string; url?: string };

export interface StaleJobFileReconciliation {
  // Baseline the caller diffs against, with files that landed in Firestore
  // after the form loaded folded back in.
  currentJob: JobV2;
  // Form payload to write, with the same files folded in.
  updatedJobForm: JobV2;
  rescuedFileCount: number;
}

const keySets = (files: AnyFile[] | undefined) => {
  const urls = new Set<string>();
  const names = new Set<string>();
  (files || []).forEach((file) => {
    if (file?.url) urls.add(file.url);
    if (file?.name) names.add(file.name);
  });
  return { urls, names };
};

// Live files absent from both the baseline and the form. Name is checked as
// well as url because updateJobV2 matches files by display name: a rescued
// file that shared a name with a form file would be mistaken for it.
const findRescuable = (
  live: AnyFile[] | undefined,
  baseline: AnyFile[] | undefined,
  form: AnyFile[] | undefined
): JobFile[] => {
  const b = keySets(baseline);
  const f = keySets(form);
  return ((live || []) as JobFile[]).filter(
    (file) =>
      !!file?.url &&
      !b.urls.has(file.url) &&
      !f.urls.has(file.url) &&
      !b.names.has(file.name) &&
      !f.names.has(file.name)
  );
};

/**
 * Counterpart of reconcileStaleJobPayments for files.
 *
 * updateJobV2 writes every file list from the edit form's snapshot. A file
 * that reached Firestore after that form was loaded (a part photo added from
 * a scanned tag, a client's portal upload) is in neither the baseline nor the
 * form, so an unrelated save would write the line item without it and the
 * reference would be lost. A file missing from the form is only a genuine
 * removal when the baseline had it too; anything live but absent from both
 * arrived later and is folded into both sides so the save carries it forward.
 *
 * Only line items the form still has are considered: a line item the form
 * deleted takes its files with it, as it always has.
 */
export const reconcileStaleJobFiles = (
  liveJob: JobV2 | null | undefined,
  currentJob: JobV2,
  updatedJobForm: JobV2
): StaleJobFileReconciliation => {
  const unchanged: StaleJobFileReconciliation = {
    currentJob,
    updatedJobForm,
    rescuedFileCount: 0,
  };
  if (!liveJob) return unchanged;

  const rescuedJobFiles = findRescuable(
    liveJob.files,
    currentJob.files,
    updatedJobForm.files
  );

  const liveLineItemsByUid = new Map(
    (liveJob.lineItems || []).map((item) => [item.uid, item])
  );
  const baselineLineItemsByUid = new Map(
    (currentJob.lineItems || []).map((item) => [item.uid, item])
  );

  const rescuedByLineItem = new Map<string, JobFile[]>();
  (updatedJobForm.lineItems || []).forEach((formLineItem) => {
    const liveLineItem = liveLineItemsByUid.get(formLineItem.uid);
    if (!liveLineItem) return;
    const rescued = findRescuable(
      liveLineItem.files,
      baselineLineItemsByUid.get(formLineItem.uid)?.files,
      formLineItem.files
    );
    if (rescued.length > 0) rescuedByLineItem.set(formLineItem.uid, rescued);
  });

  const rescuedFileCount =
    rescuedJobFiles.length +
    Array.from(rescuedByLineItem.values()).reduce(
      (sum, files) => sum + files.length,
      0
    );
  if (rescuedFileCount === 0) return unchanged;

  const withRescued = (lineItems: JobLineItem[]): JobLineItem[] =>
    lineItems.map((lineItem) => {
      const rescued = rescuedByLineItem.get(lineItem.uid);
      if (!rescued) return lineItem;
      return {
        ...lineItem,
        files: [...((lineItem.files as JobFile[]) || []), ...rescued],
      } as JobLineItem;
    });

  return {
    currentJob: {
      ...currentJob,
      files: [...((currentJob.files as JobFile[]) || []), ...rescuedJobFiles],
      lineItems: withRescued(currentJob.lineItems || []),
    },
    updatedJobForm: {
      ...updatedJobForm,
      files: [
        ...((updatedJobForm.files as JobFile[]) || []),
        ...rescuedJobFiles,
      ],
      lineItems: withRescued(updatedJobForm.lineItems || []),
    },
    rescuedFileCount,
  };
};
